import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Save,
  Trash2,
  ChevronRight,
  MapPin,
  Clock,
  Eye,
  EyeOff,
  DollarSign,
  FileText,
  Briefcase,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function JobPostingDetail() {
  const { postingId } = useParams();
  const navigate = useNavigate();
  const [posting, setPosting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedPosting, setEditedPosting] = useState(null);
  const [positionTypes, setPositionTypes] = useState([]);
  const [employmentTypes, setEmploymentTypes] = useState([]);
  const [applicants, setApplicants] = useState([]);

  useEffect(() => {
    fetchMetadata();
    fetchPosting();
    fetchApplicants();
  }, [postingId]);

  const fetchMetadata = async () => {
    try {
      const [posTypes, empTypes] = await Promise.all([
        axios.get(`${API}/ats/position-types`),
        axios.get(`${API}/ats/employment-types`),
      ]);
      setPositionTypes(posTypes.data.position_types);
      setEmploymentTypes(empTypes.data.employment_types);
    } catch (error) {
      console.error('Failed to load metadata:', error);
    }
  };

  const fetchPosting = async () => {
    try {
      const response = await axios.get(`${API}/ats/job-postings/${postingId}`);
      setPosting(response.data);
      setEditedPosting({
        ...response.data,
        coverage_areas: response.data.coverage_areas?.join(', ') || '',
      });
    } catch (error) {
      toast.error('Failed to load job posting');
      navigate('/ats/postings');
    } finally {
      setLoading(false);
    }
  };

  const fetchApplicants = async () => {
    try {
      const response = await axios.get(`${API}/ats/applicants`, {
        params: { job_posting_id: postingId, include_inactive: true },
      });
      setApplicants(response.data);
    } catch (error) {
      console.error('Failed to load applicants:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...editedPosting,
        coverage_areas: editedPosting.coverage_areas
          ? editedPosting.coverage_areas.split(',').map((a) => a.trim())
          : [],
      };
      await axios.put(`${API}/ats/job-postings/${postingId}`, payload);
      toast.success('Job posting updated successfully');
      setIsEditing(false);
      fetchPosting();
    } catch (error) {
      toast.error('Failed to update job posting');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this job posting?')) return;
    try {
      await axios.delete(`${API}/ats/job-postings/${postingId}`);
      toast.success('Job posting deleted successfully');
      navigate('/ats/postings');
    } catch (error) {
      toast.error('Failed to delete job posting');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800';
      case 'Paused':
        return 'bg-yellow-100 text-yellow-800';
      case 'Closed':
        return 'bg-red-100 text-red-800';
      case 'Draft':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Loading job posting...
      </div>
    );
  }

  if (!posting) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Job posting not found</p>
        <Button className="mt-4" onClick={() => navigate('/ats/postings')}>
          Back to Job Postings
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/ats/postings')}
            data-testid="back-btn"
          >
            <ArrowLeft size={18} className="mr-1" />
            Back
          </Button>
          <div>
            <h1 className="font-heading text-3xl font-semibold text-foreground tracking-tight">
              {posting.title}
            </h1>
            <div className="flex items-center gap-3 mt-1">
              <Badge className={getStatusColor(posting.status)}>
                {posting.status}
              </Badge>
              {posting.is_public ? (
                <span className="flex items-center gap-1 text-sm text-green-600">
                  <Eye size={14} />
                  Public
                </span>
              ) : (
                <span className="flex items-center gap-1 text-sm text-gray-500">
                  <EyeOff size={14} />
                  Private
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {isEditing ? (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  setIsEditing(false);
                  setEditedPosting({
                    ...posting,
                    coverage_areas: posting.coverage_areas?.join(', ') || '',
                  });
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-btn">
                <Save size={16} className="mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => setIsEditing(true)}
                data-testid="edit-btn"
              >
                Edit
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                data-testid="delete-btn"
              >
                <Trash2 size={16} className="mr-2" />
                Delete
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Details */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6">
            <h2 className="font-semibold text-lg mb-4">Job Details</h2>
            {isEditing ? (
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Title
                  </label>
                  <Input
                    value={editedPosting.title}
                    onChange={(e) =>
                      setEditedPosting({ ...editedPosting, title: e.target.value })
                    }
                    data-testid="edit-title"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Position Type
                    </label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={editedPosting.position_type}
                      onChange={(e) =>
                        setEditedPosting({
                          ...editedPosting,
                          position_type: e.target.value,
                        })
                      }
                      data-testid="edit-position-type"
                    >
                      {positionTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Employment Type
                    </label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={editedPosting.employment_type}
                      onChange={(e) =>
                        setEditedPosting({
                          ...editedPosting,
                          employment_type: e.target.value,
                        })
                      }
                      data-testid="edit-employment-type"
                    >
                      {employmentTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Location
                    </label>
                    <Input
                      value={editedPosting.location || ''}
                      onChange={(e) =>
                        setEditedPosting({ ...editedPosting, location: e.target.value })
                      }
                      data-testid="edit-location"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Salary Range
                    </label>
                    <Input
                      value={editedPosting.salary_range || ''}
                      onChange={(e) =>
                        setEditedPosting({
                          ...editedPosting,
                          salary_range: e.target.value,
                        })
                      }
                      data-testid="edit-salary"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Coverage Areas (comma-separated)
                  </label>
                  <Input
                    value={editedPosting.coverage_areas || ''}
                    onChange={(e) =>
                      setEditedPosting({
                        ...editedPosting,
                        coverage_areas: e.target.value,
                      })
                    }
                    data-testid="edit-coverage-areas"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Description
                  </label>
                  <textarea
                    className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={editedPosting.description || ''}
                    onChange={(e) =>
                      setEditedPosting({
                        ...editedPosting,
                        description: e.target.value,
                      })
                    }
                    data-testid="edit-description"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Requirements
                  </label>
                  <textarea
                    className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={editedPosting.requirements || ''}
                    onChange={(e) =>
                      setEditedPosting({
                        ...editedPosting,
                        requirements: e.target.value,
                      })
                    }
                    data-testid="edit-requirements"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Status
                    </label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={editedPosting.status}
                      onChange={(e) =>
                        setEditedPosting({ ...editedPosting, status: e.target.value })
                      }
                      data-testid="edit-status"
                    >
                      <option value="Active">Active</option>
                      <option value="Paused">Paused</option>
                      <option value="Closed">Closed</option>
                      <option value="Draft">Draft</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2 mt-6">
                    <input
                      type="checkbox"
                      id="is_public"
                      checked={editedPosting.is_public}
                      onChange={(e) =>
                        setEditedPosting({
                          ...editedPosting,
                          is_public: e.target.checked,
                        })
                      }
                      className="h-4 w-4"
                      data-testid="edit-is-public"
                    />
                    <label htmlFor="is_public" className="text-sm">
                      Show on public job board
                    </label>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <Briefcase size={16} className="text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Position:</span>
                    <span className="font-medium">{posting.position_type}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock size={16} className="text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Type:</span>
                    <span className="font-medium">{posting.employment_type}</span>
                  </div>
                  {posting.location && (
                    <div className="flex items-center gap-2">
                      <MapPin size={16} className="text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Location:</span>
                      <span className="font-medium">{posting.location}</span>
                    </div>
                  )}
                  {posting.salary_range && (
                    <div className="flex items-center gap-2">
                      <DollarSign size={16} className="text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Salary:</span>
                      <span className="font-medium">{posting.salary_range}</span>
                    </div>
                  )}
                </div>

                {posting.coverage_areas && posting.coverage_areas.length > 0 && (
                  <div>
                    <span className="text-sm text-muted-foreground">
                      Coverage Areas:
                    </span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {posting.coverage_areas.map((area, idx) => (
                        <Badge key={idx} variant="outline">
                          {area}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {posting.description && (
                  <div>
                    <h3 className="font-medium text-sm text-muted-foreground mb-1">
                      Description
                    </h3>
                    <p className="text-sm whitespace-pre-wrap">{posting.description}</p>
                  </div>
                )}

                {posting.requirements && (
                  <div>
                    <h3 className="font-medium text-sm text-muted-foreground mb-1">
                      Requirements
                    </h3>
                    <p className="text-sm whitespace-pre-wrap">{posting.requirements}</p>
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Stats Card */}
          <Card className="p-6">
            <h2 className="font-semibold text-lg mb-4">Statistics</h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Total Applicants</span>
                <span className="font-semibold text-lg">{applicants.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Created</span>
                <span className="text-sm">
                  {new Date(posting.created_at).toLocaleDateString()}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Last Updated</span>
                <span className="text-sm">
                  {new Date(posting.updated_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          </Card>

          {/* Applicants Card */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-lg">Applicants</h2>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/ats/applicants?posting=${postingId}`)}
                data-testid="view-all-applicants"
              >
                View All
              </Button>
            </div>
            {applicants.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No applicants yet
              </p>
            ) : (
              <div className="space-y-2">
                {applicants.slice(0, 5).map((applicant) => (
                  <div
                    key={applicant.id}
                    className="flex items-center justify-between p-2 rounded hover:bg-muted/50 cursor-pointer"
                    onClick={() => navigate(`/ats/applicants/${applicant.id}`)}
                    data-testid={`applicant-link-${applicant.id}`}
                  >
                    <div>
                      <p className="font-medium text-sm text-primary hover:underline">
                        {applicant.first_name} {applicant.last_name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {applicant.status}
                      </p>
                    </div>
                    <ChevronRight size={14} className="text-muted-foreground" />
                  </div>
                ))}
                {applicants.length > 5 && (
                  <p className="text-xs text-muted-foreground text-center pt-2">
                    +{applicants.length - 5} more
                  </p>
                )}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
